import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Form, Formik } from 'formik'

import {
  Button,
  Checkbox,
  Modal,
  useShowMessage,
  useUpdateReport,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { TextField } from 'components/FormFields/FormFields'
import { initialKeywordValue, keywordValidationSchema } from './schema'
import styles from './AddKeyword.module.css'

import type { Keyword } from '@pepper/utils/src/types/seo'

interface Props {
  reportId: string
  type: 'secondary' | 'terms'
  keywords: {
    secondary: Keyword[]
    terms: Keyword[]
  }
  onSuccess: () => void
}

function AddKeywordModal({ reportId, type, keywords, onSuccess }: Props) {
  const companyId = useSelectedCompanyId()
  const showMessage = useShowMessage()
  const modal = useModal()

  const updateReport = useUpdateReport()

  const handleSubmit = (values: Keyword) => {
    const isKeywordDuplicate = [
      ...keywords.secondary.filter(item => item.keyword === values.keyword),
      ...keywords.terms.filter(item => item.keyword === values.keyword),
    ]
    if (isKeywordDuplicate.length > 0) {
      showMessage({ type: 'error', message: 'Keyword already present' })
      return
    }
    const _keywords = { ...keywords }
    // values returned by formik are string, so we need to convert them to number
    _keywords[type].push({
      keyword: values.keyword,
      min: +values.min,
      max: +values.max,
      heading_presence: +values.heading_presence,
    })
    updateReport.mutate(
      {
        companyId,
        reportId,
        body: {
          document: {
            secondary_keywords: _keywords.secondary,
            terms: _keywords.terms,
          },
        },
      },
      {
        onSuccess: () => {
          onSuccess()
          modal.remove()
        },
        onError: error => {
          if (error instanceof Error) {
            showMessage({ type: 'error', message: error.message })
          }
        },
      }
    )
  }

  return (
    <Modal
      title="Add a new keyword"
      visible={modal.visible}
      afterClose={modal.remove}
      width={520}
      footer={null}
    >
      <Formik<Keyword>
        // adding empty string value to min & max fields to make the initial field value empty instead of 0
        initialValues={initialKeywordValue as unknown as Keyword}
        validationSchema={keywordValidationSchema}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, resetForm }) => {
          return (
            <Form>
              <div>
                <TextField
                  name="keyword"
                  label="Add Keyword"
                  required={true}
                  fieldProps={{
                    placeholder: 'Media',
                    loadV2: true,
                    value: values.keyword.toLowerCase(),
                  }}
                  className={styles.textField}
                />
                <Checkbox
                  name="heading_presence"
                  value={values.heading_presence}
                  defaultChecked={Boolean(values.heading_presence)}
                  className={styles.checkboxField}
                  onChange={event =>
                    setFieldValue('heading_presence', +event.target.checked)
                  }
                >
                  Include keyword in heading
                </Checkbox>
              </div>

              <div className={styles.textFieldWrapper}>
                <TextField
                  name="min"
                  label="Minimum usage frequency"
                  className={styles.textField}
                  required={true}
                  fieldProps={{ placeholder: '3', loadV2: true }}
                />
                <TextField
                  name="max"
                  label="Maximum usage frequency"
                  className={styles.textField}
                  required={true}
                  fieldProps={{ placeholder: '5', loadV2: true }}
                />
              </div>

              <div className={styles.ctaContainer}>
                <Button
                  type="link"
                  onClick={() => {
                    resetForm()
                    modal.remove()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={updateReport.isLoading}
                  loading={updateReport.isLoading}
                >
                  Add Keyword
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default NiceModal.create(AddKeywordModal)
